import type { CountryTimezone, SupportedCountry } from './countries.js';
import {
  DEFAULT_COUNTRY_CODE,
  DEFAULT_CURRENCY,
  DEFAULT_TIMEZONE,
  SUPPORTED_COUNTRIES,
} from './countries.js';

// Display names for known ISO 4217 currencies. Only currencies used by
// SUPPORTED_COUNTRIES need an entry here — adding a country with a new
// currency should also add its label here (falls back to the bare code
// otherwise, so nothing breaks if it's forgotten).
const CURRENCY_NAMES: Record<string, string> = {
  USD: 'US Dollar',
};

export function getEnabledCountries(): SupportedCountry[] {
  return [...SUPPORTED_COUNTRIES];
}

export function getCountry(code: string): SupportedCountry | undefined {
  const normalized = code.toUpperCase();
  return SUPPORTED_COUNTRIES.find((country) => country.code === normalized);
}

export function isSupportedCountry(code: string): boolean {
  return getCountry(code) !== undefined;
}

export function getCountryOptions(): { value: string; label: string }[] {
  return SUPPORTED_COUNTRIES.map((country) => ({ value: country.code, label: country.name }));
}

export function getTimezoneOptions(countryCode: string = DEFAULT_COUNTRY_CODE): CountryTimezone[] {
  const country = getCountry(countryCode);
  return country ? [...country.timezones] : [];
}

export function getCurrencyOptions(): { value: string; label: string }[] {
  const currencies = new Set(SUPPORTED_COUNTRIES.map((country) => country.currency));
  return [...currencies].map((currency) => ({
    value: currency,
    label: CURRENCY_NAMES[currency] ? `${currency} — ${CURRENCY_NAMES[currency]}` : currency,
  }));
}

export function getCountryCurrency(code: string): string {
  return getCountry(code)?.currency ?? DEFAULT_CURRENCY;
}

export function getDefaultTimezone(code: string = DEFAULT_COUNTRY_CODE): string {
  return getCountry(code)?.defaultTimezone ?? DEFAULT_TIMEZONE;
}

export function isSupportedTimezone(
  tz: string,
  countryCode: string = DEFAULT_COUNTRY_CODE,
): boolean {
  return getTimezoneOptions(countryCode).some((timezone) => timezone.value === tz);
}

export function isSupportedCurrency(currency: string): boolean {
  const normalized = currency.toUpperCase();
  return SUPPORTED_COUNTRIES.some((country) => country.currency === normalized);
}
